import { useZustandLocation } from "@faire/retailer-visitor-shared/app/_lib/routing/useZustandLocation";
import { useFaireDirectFirstOrderIncentive } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import { useFaireDirectMarketplaceIncentive } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectMarketplaceIncentive/useFaireDirectMarketplaceIncentive";
import { LOCAL_STORAGE_KEY } from "@faire/retailer-visitor-shared/lib/localSession";
import { useSettingFaireDirectRecurringMarketplaceEligibleRoutes } from "@faire/retailer-visitor-shared/settings/useSettingFaireDirectRecurringMarketplaceEligibleRoutes";
import { useSettingFaireDirectRecurringMarketplaceModalVisitThreshold } from "@faire/retailer-visitor-shared/settings/useSettingFaireDirectRecurringMarketplaceModalVisitThreshold";
import { useMilestone } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { usePromoModalStore } from "@faire/retailer-visitor-shared/stores/ui/__internal__/usePromoModalStore";
import { useFetchCachedBrandView } from "@faire/retailer-visitor-shared/views/brand/hooks/useFetchCachedBrandView";
import { isReorderArchetype } from "@faire/retailer-visitor-shared/views/brand/isReorderArchetype";
import { useLeanProductQuery } from "@faire/retailer-visitor-shared/views/product/utils/useLeanProductQuery";
import { Storage } from "@faire/web--source/common/Storage";
import { isNullish } from "@faire/web--source/common/typescriptUtils";
import { matchPathWithLocale } from "@faire/web--source/ui/routing/react-utils";
import { removeLocaleFromUrl } from "@faire/web--source/ui/routing/util";
import { FD_MARKETPLACE_DISCOUNT_MODAL_DISMISSED } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/FD_MARKETPLACE_DISCOUNT_MODAL_DISMISSED";
import { match as matchBrandRoute } from "@faire/web-api--source/routes/brand/brandToken/match";
import { match as matchProductRoute } from "@faire/web-api--source/routes/product/productToken/match";
import { useMemo } from "react";
export var useShouldNotShowFaireDirectIncentiveModals = function() {
    var _useFaireDirectFirstOrderIncentive = useFaireDirectFirstOrderIncentive(), hasFaireDirectFirstOrderIncentive = _useFaireDirectFirstOrderIncentive.hasIncentive;
    var _useFaireDirectMarketplaceIncentive = useFaireDirectMarketplaceIncentive(), hasFaireDirectMarketplaceIncentive = _useFaireDirectMarketplaceIncentive.hasIncentive;
    var isEligibleForTargetedMarketplaceModal = useIsEligibleForTargetedFaireDirectRecurringMarketplaceModal();
    var _useMilestone = useMilestone(FD_MARKETPLACE_DISCOUNT_MODAL_DISMISSED), hasDismissedFaireDirectMarketplaceModal = _useMilestone.hasOccurred;
    var modalToDisplay = usePromoModalStore().modalToDisplay;
    var shouldNotShowFaireDirectFirstOrderIncentiveModal = modalToDisplay === "FAIRE_DIRECT_INCENTIVE_MODAL" && !hasFaireDirectFirstOrderIncentive;
    var shouldHideModalNoMarketplaceIncentive = (modalToDisplay === "FAIRE_DIRECT_MARKETPLACE_DISCOUNT_MODAL" || modalToDisplay === "FAIRE_DIRECT_EVERGREEN_MARKETPLACE_DISCOUNT_MODAL") && !hasFaireDirectMarketplaceIncentive;
    // if retailer is eligible to see the marketplace modal, recalculate
    var shouldHideTargetedMarketplaceDiscountModal = modalToDisplay === "FAIRE_DIRECT_MARKETPLACE_DISCOUNT_MODAL" && hasDismissedFaireDirectMarketplaceModal && !isEligibleForTargetedMarketplaceModal;
    var shouldNotShowFaireDirectMarketplaceIncentiveModal = shouldHideModalNoMarketplaceIncentive || shouldHideTargetedMarketplaceDiscountModal;
    return shouldNotShowFaireDirectFirstOrderIncentiveModal || shouldNotShowFaireDirectMarketplaceIncentiveModal;
};
// Custom hook to check if retailer is eligible for the targeted FD Marketplace modal
var useIsEligibleForTargetedFaireDirectRecurringMarketplaceModal = function() {
    var _matchBrandRoute, _matchProductRoute;
    var location = useZustandLocation(function(state) {
        return state.pathname;
    });
    var eligibleRoutes = useSettingFaireDirectRecurringMarketplaceEligibleRoutes();
    var threshold = useSettingFaireDirectRecurringMarketplaceModalVisitThreshold();
    var lastVisitAt = getLastTimeSeen();
    var currentPath = location.pathname;
    var brandTokenMatch = (_matchBrandRoute = matchBrandRoute(removeLocaleFromUrl(currentPath))) === null || _matchBrandRoute === void 0 ? void 0 : _matchBrandRoute.params.brandToken;
    var productTokenMatch = (_matchProductRoute = matchProductRoute(removeLocaleFromUrl(currentPath))) === null || _matchProductRoute === void 0 ? void 0 : _matchProductRoute.params.productToken;
    var _useLeanProductQuery = useLeanProductQuery(productTokenMatch), productData = _useLeanProductQuery.data;
    var brandToken = useMemo(function() {
        var _productData_brand;
        if (brandTokenMatch) {
            return brandTokenMatch;
        } else if (productData === null || productData === void 0 ? void 0 : (_productData_brand = productData.brand) === null || _productData_brand === void 0 ? void 0 : _productData_brand.token) {
            return productData.brand.token;
        }
        return undefined;
    }, [
        brandTokenMatch,
        productData
    ]);
    var _useFetchCachedBrandView = useFetchCachedBrandView(brandToken !== null && brandToken !== void 0 ? brandToken : ""), brandViewData = _useFetchCachedBrandView.data;
    if (lastVisitAt === null) {
        return false;
    }
    var timeSinceLastVisit = Date.now() - lastVisitAt;
    if (timeSinceLastVisit < threshold) {
        return false;
    }
    /* Check if route is
     ** 1. Homepage or
     ** 2. In one of the allowed routes or
     ** 3. In one of the not-ordered allowed routes without a previous order from the brand
     */ var eligible = currentPath === "/" || eligibleRoutes.some(function(path) {
        return matchPathWithLocale(currentPath, path);
    }) || !isNullish(brandViewData) && !isReorderArchetype(brandViewData);
    return eligible;
};
var getLastTimeSeen = function() {
    var sessionData;
    try {
        var _Storage_local_getItem;
        sessionData = JSON.parse((_Storage_local_getItem = Storage.local.getItem(LOCAL_STORAGE_KEY)) !== null && _Storage_local_getItem !== void 0 ? _Storage_local_getItem : "");
    } catch (e) {
        return null;
    }
    var lastVisitAt = sessionData["updated-at"];
    if (isNullish(lastVisitAt)) {
        return null;
    }
    return Number(lastVisitAt) || 0;
};
